import { useEffect, useState } from "react";
import { aiFetch } from "./ai";

// ─── TEXTOS GENERADOS EN EL IDIOMA DE LA PANTALLA (23/10) ──────────────────
// Lo que escribe la IA (fortalezas, áreas a explorar, análisis de
// referencias…) se guarda en el idioma en que se generó, y se pintaba tal cual
// dentro de una pantalla en el otro: mitad español, mitad inglés.
//
// useLocalized(texts, lang) devuelve los mismos textos en el idioma de la
// pantalla. Si ya lo están, no hace nada. Si no, los traduce UNA vez con la IA
// y guarda la traducción en el navegador (localStorage), así que la siguiente
// vez sale al instante y sin gastar. Mientras traduce, enseña el original.
// Solo para textos generados; lo que escribe una persona no se traduce.

// ¿En qué idioma está? Cuenta palabras frecuentes de cada uno; los acentos y
// la ñ cuentan para español. null si no se puede saber (vacío, nombres…).
const ES_WORDS = /\b(de|la|el|los|las|que|en|con|para|por|una?|su|sus|del|al|es|son|como|más|sin|pero|muy|ha|han)\b/gi;
const EN_WORDS = /\b(the|and|of|to|in|with|for|is|are|a|an|his|her|their|has|have|but|very|without|as|from|on|at)\b/gi;
export const detectLang = (text) => {
  const s = String(text || "");
  if (!s.trim()) return null;
  const es = (s.match(ES_WORDS) || []).length + (s.match(/[áéíóúñ¿¡]/gi) || []).length * 2;
  const en = (s.match(EN_WORDS) || []).length;
  // Sin ninguna señal de español y con frase de verdad (4+ palabras): inglés
  if (es === 0 && (s.match(/[a-z]{2,}/gi) || []).length >= 4) return "en";
  if (es === en) return null;
  return es > en ? "es" : "en";
};

const hash = (s) => {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36);
};
const readCache = (k) => { try { return JSON.parse(localStorage.getItem(k) || "null"); } catch (e) { return null; } };
const writeCache = (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch (e) { /* sin hueco: se traduce otra vez */ } };

// Una sola petición en vuelo por texto, aunque lo pidan varias pantallas a la vez
const inFlight = {};

export function useLocalized(texts, lang) {
  const list = (Array.isArray(texts) ? texts : []).map(x => String(x ?? ""));
  const joined = list.join("␞");
  const target = lang === "es" ? "es" : "en";
  const needs = !!joined.trim() && (detectLang(joined) || target) !== target;
  const key = `rt-tr:${target}:${hash(joined)}`;
  // La traducción va con su clave: al cambiar de idioma o de texto no se
  // enseña la de antes
  const [done, setDone] = useState(() => (needs ? { key, arr: readCache(key) } : null));

  useEffect(() => {
    if (!needs) return undefined;
    const cached = readCache(key);
    if (cached) { setDone({ key, arr: cached }); return undefined; }
    let alive = true;
    if (!inFlight[key]) {
      inFlight[key] = aiFetch({
        body: JSON.stringify({
          max_tokens: 1500,
          messages: [{ role: "user", content: `Translate each string of this JSON array into ${target === "es" ? "Spanish (Spain)" : "English"}. Keep the meaning, tone and length; keep names, companies and numbers as they are. Return ONLY a JSON array of strings, same length and order, no markdown.\n\n${JSON.stringify(list)}` }],
        }),
      })
        .then(r => r.json())
        .then(d => {
          const raw = (d.content || []).filter(b => b.type === "text").map(b => b.text).join("");
          const arr = JSON.parse(raw.replace(/```json|```/g, "").trim());
          if (!Array.isArray(arr) || arr.length !== list.length) throw new Error("bad translation");
          writeCache(key, arr);
          return arr;
        })
        .catch(() => null)
        .finally(() => { setTimeout(() => { delete inFlight[key]; }, 0); });
    }
    inFlight[key].then(arr => { if (alive && arr) setDone({ key, arr }); });
    return () => { alive = false; };
  }, [key, needs]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!needs) return list;
  const arr = done?.key === key ? (done.arr || readCache(key)) : readCache(key);
  return arr && arr.length === list.length ? arr : list;
}
